import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { TOONS } from '@/data/content';

export default function ToonModal({ toon, onClose }) {
  const [burst, setBurst] = useState([]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [onClose]);

  useEffect(() => {
    if (!toon) return;
    const particles = [...Array(16)].map((_, i) => ({
      angle: (i / 16) * Math.PI * 2,
      dist: 90 + Math.random() * 120,
      size: 3 + Math.random() * 5,
      id: i
    }));
    setBurst(particles);
    const t = setTimeout(() => setBurst([]), 900);
    return () => clearTimeout(t);
  }, [toon?.id]);

  if (!toon) return null;

  return (
    <motion.div
      data-testid="toon-modal"
      className="fixed inset-0 z-[280] flex items-center justify-center p-4 sm:p-8"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.35 }}
      role="dialog"
      aria-modal="true"
      aria-label={`Toon profile — ${toon.role}`}
    >
      <motion.div
        className="absolute inset-0 bg-navy/85"
        style={{ backdropFilter: 'blur(20px)' }}
        onClick={onClose}
        data-testid="toon-modal-backdrop"
      />
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0"
        style={{
          background: `radial-gradient(55% 55% at 30% 50%, ${toon.accentSoft.replace('0.16', '0.3')} 0%, transparent 70%)`
        }}
      />
      <div className="pointer-events-none absolute left-1/2 top-1/2" aria-hidden="true">
        {burst.map((p) => (
          <motion.span
            key={p.id}
            className="absolute rounded-full"
            style={{ width: p.size, height: p.size, backgroundColor: toon.accent }}
            initial={{ x: 0, y: 0, opacity: 1, scale: 1 }}
            animate={{
              x: Math.cos(p.angle) * p.dist,
              y: Math.sin(p.angle) * p.dist,
              opacity: 0,
              scale: 0.2
            }}
            transition={{ duration: 0.85, ease: 'easeOut' }}
          />
        ))}
      </div>

      <motion.div
        data-testid="toon-modal-panel"
        className="relative z-10 grid max-h-[90vh] w-full max-w-4xl grid-cols-1 overflow-y-auto rounded-3xl border bg-navy2/85 md:grid-cols-[0.9fr_1.1fr]"
        initial={{ opacity: 0, scale: 0.9, y: 32 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.94, y: 20 }}
        transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
        style={{
          borderColor: `${toon.accent}55`,
          backdropFilter: 'blur(24px)',
          boxShadow: `0 0 70px ${toon.accent}33, inset 0 0 40px rgba(0,200,255,0.04)`
        }}
      >
        <button
          data-testid="toon-modal-close"
          onClick={onClose}
          aria-label="Close Toon profile"
          className="absolute right-4 top-4 z-20 flex h-10 w-10 items-center justify-center rounded-full border border-electric/30 bg-navy/70 text-snow transition-all duration-200 hover:border-gold hover:text-gold"
        >
          <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2.2">
            <path d="M6 6l12 12M18 6L6 18" />
          </svg>
        </button>

        <div
          className="relative flex items-center justify-center overflow-hidden p-8"
          style={{ background: `radial-gradient(70% 70% at 50% 55%, ${toon.accentSoft} 0%, transparent 75%)` }}
        >
          <motion.img
            src={toon.imageFull}
            alt={`${toon.role} — full character artwork`}
            className="max-h-[46vh] w-auto object-contain md:max-h-[58vh]"
            animate={{ y: [0, -10, 0] }}
            transition={{ duration: 4, repeat: Infinity, ease: 'easeInOut' }}
            style={{ filter: `drop-shadow(0 0 34px ${toon.accent}66)` }}
          />
        </div>

        <div className="flex flex-col justify-center p-8 sm:p-10">
          <div className="flex items-center gap-3">
            <span
              className="rounded-full border px-3 py-1 font-mono text-[9px] uppercase tracking-[0.3em]"
              style={{ borderColor: `${toon.accent}66`, color: toon.accent }}
              data-testid="toon-modal-index"
            >
              TOON FILE // {toon.index}
            </span>
            <span
              className="rounded-full border border-dashed border-gold/60 px-3 py-1 font-mono text-[9px] uppercase tracking-[0.3em] text-gold"
              data-testid="toon-modal-status"
            >
              {toon.status}
            </span>
          </div>
          <h3
            data-testid="toon-modal-role"
            className="mt-5 font-display text-3xl font-extrabold uppercase tracking-tight sm:text-4xl"
            style={{ color: toon.accent, textShadow: `0 0 30px ${toon.accent}55` }}
          >
            {toon.role}
          </h3>
          <p className="mt-2 text-sm font-semibold text-snow/85" data-testid="toon-modal-tagline">
            {toon.tagline}
          </p>
          <div className="mt-5 rounded-2xl border border-electric/15 bg-navy/50 p-4">
            <p className="font-mono text-[9px] uppercase tracking-[0.3em] text-electric">PERSONALITY</p>
            <p className="mt-2 text-sm text-snow/75" data-testid="toon-modal-personality">
              {toon.personality}
            </p>
          </div>
          <div className="mt-4">
            <p className="font-mono text-[9px] uppercase tracking-[0.3em] text-electric">LORE</p>
            <p className="mt-2 text-sm leading-relaxed text-snow/70" data-testid="toon-modal-lore">
              {toon.lore}
            </p>
          </div>
          <p className="mt-6 font-mono text-[10px] uppercase tracking-[0.25em] text-snow/40">
            FULL STORY — {toon.status.toLowerCase()}
          </p>
        </div>
      </motion.div>
    </motion.div>
  );
}
